import type {
  EconomyConfig,
  EconomySegment,
  RankPayout,
  RelegationConfig,
  EconomyBreakdown,
  SegmentBreakdown,
  EconomyPreset,
  PresetOption,
} from '@/types/economy'

// ---------------------------------------------------------------------------
// Preset Defaults
// ---------------------------------------------------------------------------

const BALANCED_SEGMENTS: EconomySegment[] = [
  {
    key: '1h',
    label: 'First Half',
    enabled: true,
    pot_percentage: 22.7,
    ranks: [
      { place: 1, percentage: 50 },
      { place: 2, percentage: 30 },
      { place: 3, percentage: 20 },
    ],
  },
  {
    key: '2h',
    label: 'Second Half',
    enabled: true,
    pot_percentage: 22.7,
    ranks: [
      { place: 1, percentage: 50 },
      { place: 2, percentage: 30 },
      { place: 3, percentage: 20 },
    ],
  },
  {
    key: 'overall',
    label: 'Overall Season',
    enabled: true,
    pot_percentage: 54.6,
    ranks: [
      { place: 1, percentage: 50 },
      { place: 2, percentage: 30 },
      { place: 3, percentage: 20 },
    ],
  },
]

const END_FOCUSED_END_ONLY_SEGMENTS: EconomySegment[] = [
  { key: '1h', label: 'First Half', enabled: false, pot_percentage: 0, ranks: [] },
  { key: '2h', label: 'Second Half', enabled: false, pot_percentage: 0, ranks: [] },
  {
    key: 'overall',
    label: 'Overall Season',
    enabled: true,
    pot_percentage: 100,
    ranks: [
      { place: 1, percentage: 60 },
      { place: 2, percentage: 25 },
      { place: 3, percentage: 15 },
    ],
  },
]

const END_FOCUSED_HYBRID_SEGMENTS: EconomySegment[] = [
  { key: '1h', label: 'First Half', enabled: false, pot_percentage: 0, ranks: [] },
  {
    key: '2h',
    label: 'Second Half',
    enabled: true,
    pot_percentage: 30,
    ranks: [
      { place: 1, percentage: 60 },
      { place: 2, percentage: 25 },
      { place: 3, percentage: 15 },
    ],
  },
  {
    key: 'overall',
    label: 'Overall Season',
    enabled: true,
    pot_percentage: 70,
    ranks: [
      { place: 1, percentage: 60 },
      { place: 2, percentage: 25 },
      { place: 3, percentage: 15 },
    ],
  },
]

const DEFAULT_RELEGATION: RelegationConfig = {
  enabled: false,
  mode: 'owl_penalty',
  penalty_amount: 0,
  future_buy_in_increase: 0,
}

export function getPresetSegments(
  preset: EconomyPreset,
  option: PresetOption,
): EconomySegment[] {
  if (preset === 'balanced_segmented') return structuredClone(BALANCED_SEGMENTS)
  if (option === 'hybrid') return structuredClone(END_FOCUSED_HYBRID_SEGMENTS)
  return structuredClone(END_FOCUSED_END_ONLY_SEGMENTS)
}

export function createDefaultConfig(
  preset: EconomyPreset = 'balanced_segmented',
  option: PresetOption = null,
): EconomyConfig {
  return {
    version: 1,
    buy_in: 100,
    currency_label: 'owls',
    preset,
    preset_option: option,
    segments: getPresetSegments(preset, option),
    relegation: { ...DEFAULT_RELEGATION },
    locked: false,
    locked_at: null,
  }
}

// ---------------------------------------------------------------------------
// Calculations
// ---------------------------------------------------------------------------

export function calculateTotalPot(buyIn: number, playerCount: number): number {
  return buyIn * playerCount
}

export function calculateSegmentPot(totalPot: number, potPercentage: number): number {
  return Math.floor(totalPot * (potPercentage / 100))
}

export function calculateRankPayouts(
  segmentPot: number,
  ranks: RankPayout[],
): { place: number; owls: number; percentage: number }[] {
  if (ranks.length === 0 || segmentPot === 0) return []

  const payouts = ranks.map((r) => ({
    place: r.place,
    owls: Math.floor(segmentPot * (r.percentage / 100)),
    percentage: r.percentage,
  }))

  // Remainder owls go to 1st place
  const distributed = payouts.reduce((sum, p) => sum + p.owls, 0)
  const remainder = segmentPot - distributed
  if (remainder !== 0 && payouts.length > 0) {
    payouts[0].owls += remainder
  }

  return payouts
}

export function calculateBreakdown(
  config: EconomyConfig,
  playerCount: number,
): EconomyBreakdown {
  const totalPot = calculateTotalPot(config.buy_in, playerCount)

  const enabledSegments = config.segments.filter((s) => s.enabled)

  const segments: SegmentBreakdown[] = enabledSegments.map((seg) => {
    const segPot = calculateSegmentPot(totalPot, seg.pot_percentage)
    return {
      key: seg.key,
      label: seg.label,
      owls: segPot,
      percentage: seg.pot_percentage,
      ranks: calculateRankPayouts(segPot, seg.ranks),
    }
  })

  // Only apply rounding correction when segment percentages sum to ~100%
  const percentageSum = enabledSegments.reduce((sum, s) => sum + s.pot_percentage, 0)
  if (Math.abs(percentageSum - 100) <= 0.1) {
    const allocated = segments.reduce((sum, s) => sum + s.owls, 0)
    const remainder = totalPot - allocated
    if (remainder !== 0 && segments.length > 0) {
      const largest = segments.reduce((a, b) => (a.owls >= b.owls ? a : b))
      largest.owls += remainder
      const seg = enabledSegments.find((s) => s.key === largest.key)
      if (seg) {
        largest.ranks = calculateRankPayouts(largest.owls, seg.ranks)
      }
    }
  }

  return {
    totalPot,
    buyIn: config.buy_in,
    playerCount,
    currencyLabel: config.currency_label,
    segments,
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidationError {
  field: string
  message: string
}

export function validateConfig(config: EconomyConfig): ValidationError[] {
  const errors: ValidationError[] = []

  if (config.buy_in <= 0) {
    errors.push({ field: 'buy_in', message: 'Buy-in must be greater than 0' })
  }

  const enabledSegments = config.segments.filter((s) => s.enabled)

  if (enabledSegments.length === 0) {
    errors.push({ field: 'segments', message: 'At least one segment must be enabled' })
  }

  const segmentSum = enabledSegments.reduce((sum, s) => sum + s.pot_percentage, 0)
  if (Math.abs(segmentSum - 100) > 0.1) {
    errors.push({
      field: 'segments',
      message: `Segment percentages sum to ${segmentSum.toFixed(1)}%, must equal 100%`,
    })
  }

  for (const seg of enabledSegments) {
    if (seg.ranks.length === 0) {
      errors.push({
        field: `segment.${seg.key}.ranks`,
        message: `${seg.label} must have at least one rank`,
      })
      continue
    }

    const rankSum = seg.ranks.reduce((sum, r) => sum + r.percentage, 0)
    if (Math.abs(rankSum - 100) > 0.1) {
      errors.push({
        field: `segment.${seg.key}.ranks`,
        message: `${seg.label} rank percentages sum to ${rankSum.toFixed(1)}%, must equal 100%`,
      })
    }

    for (const rank of seg.ranks) {
      if (rank.percentage < 0) {
        errors.push({
          field: `segment.${seg.key}.rank.${rank.place}`,
          message: `${seg.label} rank ${rank.place} cannot have negative percentage`,
        })
      }
    }
  }

  return errors
}
